'use client';

import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPackPerformance, setPage } from '../../../../../apps/frontend/src/redux/slices/packSlice';
import { useNavigate } from 'react-router-dom';
import TestFilterTabs from '../generic/TestFilterTabs';
import PerformanceSummary from './PerformanceSummary';
import PerformanceTestBreakdown from './PerformanceTestBreakdown';
import PackPagination from '../generic/PackPagination';
import PackButton from '../generic/PackButton';

const Performance = ({ pack, packId: propPackId, isChapterWiseTestSeries, onResetFilters }) => {
  const dispatch = useDispatch();
  const router = useNavigate();
  const [showPercentage, setShowPercentage] = useState(true);
  const [showPercentageTestWiseBreakdown, setShowPercentageTestWiseBreakdown] = useState(false);
  const [currentTab, setCurrentTab] = useState('overall');

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(true);
  const [isTabletHorizontal, setIsTabletHorizontal] = useState(false);
  const scrollContainerRef = useRef(null);

  const {
    performance: { data, loading },
    filters,
  } = useSelector((state) => state.pack);

  const packId = propPackId || localStorage.getItem('selectedPackId');

  // Device detection effect
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      // Tablet Horizontal: width between 800-1366px and width > height
      const isTabletHoriz = width >= 800 && width <= 1366 && width > height;
      setIsTabletHorizontal(isTabletHoriz);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  useEffect(() => {
    if (!packId) return;
    const fetchData = async () => {
      try {
        const resultAction = await dispatch(fetchPackPerformance({ packId, page: filters?.page || 1 }));
        if (resultAction?.type?.includes('rejected')) {
          // Handle error
        }
      } catch (error) {
        // Handle error
      }
    };
    fetchData();
  }, [dispatch, filters, packId]);

  // Scroll handler for header visibility
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const currentScrollY = scrollContainerRef.current.scrollTop;
      const isDesktop = window.innerWidth >= 1367; // Above tablet horizontal max width

      if (isDesktop) {
        // Desktop: hide description on scroll down
        if (currentScrollY <= 0 && !isDescriptionVisible) setIsDescriptionVisible(true);
        else if (currentScrollY > 70 && isDescriptionVisible) setIsDescriptionVisible(false);
        else if (currentScrollY < 30 && !isDescriptionVisible) setIsDescriptionVisible(true);

        setIsHeaderVisible(true);
        return;
      }

      // Mobile/Tablet behavior (both portrait and landscape)
      if (currentScrollY < lastScrollY) setIsHeaderVisible(true);
      else if (currentScrollY > lastScrollY && currentScrollY > 100) setIsHeaderVisible(false);

      setLastScrollY(currentScrollY);
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [lastScrollY, isDescriptionVisible]);

  // Determine if back button should be shown
  // Show on mobile/tablet portrait (lg:hidden) OR tablet horizontal
  const shouldShowBackButton = window.innerWidth < 1024 || isTabletHorizontal;

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col h-screen overflow-y-auto" ref={scrollContainerRef}>
      {/* Header */}
      <div
        className={`z-10 q3-glass rounded transition-transform duration-300 ease-in-out ${
          // Only apply transform on non-desktop devices
          window.innerWidth >= 1367 ? '' : isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="p-6 pb-4">
          {/* Back Button - Show on Mobile/Tablet Portrait + Tablet Horizontal */}
          <PackButton navigateTo="/v2/home" />

          {/* Page Title */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-[var(--q3-neutral-default)]">Performance</h1>
          </div>

          {/* TestFilterTabs */}
          {/* <div className={`transition-all duration-300 ease-in-out ${isDescriptionVisible ? '' : 'lg:-mt-4'}`}>
            <TestFilterTabs />
          </div> */}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="p-6 pt-2">
        <div className="space-y-8">
          <PerformanceSummary
            data={data?.summary}
            loading={loading}
            showPercentage={showPercentage}
            onResetFilters={onResetFilters}
          />

          <PerformanceTestBreakdown
            pack={pack}
            data={data}
            currentTab={currentTab}
            loading={loading}
            showPercentage={showPercentageTestWiseBreakdown}
            onTogglePercentage={setShowPercentageTestWiseBreakdown}
            onSubjectChange={setCurrentTab}
            onResetFilters={onResetFilters}
          />

          {data?.totalPagesAvailable > 1 && (
            <PackPagination
              totalPages={data?.totalPagesAvailable}
              activePage={filters?.page}
              onPageClick={(page) => dispatch(setPage(page))}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Performance;
